'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';

import { countryLabel, countryFlagCode } from 'src/utils/platform/channel-label';

import { RADIUS } from 'src/theme/ui-tokens';

import { FlagIcon } from 'src/components/flag-icon';

/**
 * 국가 한 칸 — **국기 + 대문자 코드**. 화면 어디서나 나라는 이 모양으로 적는다.
 *
 * 🔑 조건 칸(사이드바 국가 필터)이 국기 + 코드라 결과 쪽도 같아야 한다 — 고른 것과 나온 것이
 *   같은 모양이어야 「내가 US 를 골랐는데 왜 미국이 없지」가 안 생긴다.
 * ⚠️ 나라를 못 가린 값은 「미상」이고 **국기가 없다** — 두 글자 코드가 아니면 국기 그림이 없어
 *   깨진 그림이 뜬다(`countryFlagCode` 가 거른다).
 */
export function CountryTag({ code, size = 14 }: { code: string | null | undefined; size?: number }) {
  const flag = countryFlagCode(code);

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {flag && <FlagIcon code={flag} sx={{ width: size, height: Math.round((size * 3) / 4), borderRadius: RADIUS.sm, flexShrink: 0 }} />}
      <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {countryLabel(code)}
      </Box>
    </Box>
  );
}

/**
 * 고르개에 세우는 나라 칸 한 벌 — 화면마다 목록을 빚지 않는다.
 *
 * 🔑 **고르는 쪽과 나온 쪽이 같은 모양**이어야 한다 — 조건 칸에서 국기 + `KR` 로 고르고
 *   결과에는 「한국」으로 나오면 같은 조건인지 사용자가 못 가린다.
 * ⚠️ `label` 은 **접힌 칸**이 쓰는 글자다(「KR 외 2」) · `node` 는 펼친 목록이 그리는 국기 + 코드다.
 */
export function toCountryOptions(codes: readonly string[]): { value: string; label: string; node: ReactNode }[] {
  return codes.map((code) => ({
    value: code,
    label: countryLabel(code),
    node: <CountryTag code={code} />,
  }));
}
